<template>
  <Wrapper :team="team">
    <div>
      <!-- Profile section -->
      <div class="mt-10 divide-y divide-gray-200">
        <div class="space-y-1">
          <h3 class="text-lg font-medium leading-6 text-gray-900">
            Team profile
          </h3>
          <p class="max-w-2xl text-sm text-gray-500">
            Name and logo will be displayed publicly in TravelOffice.
          </p>
        </div>
        <div class="mt-6">
          <dl class="divide-y divide-gray-200">
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt class="text-sm font-medium text-gray-500">
                Team name
              </dt>
              <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <editable-field
                  v-if="can('update team')"
                  :form="form"
                  attribute="name"
                  @submit.prevent="updateTeam" />
                <span v-else>{{ team.name }}</span>
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt class="text-sm font-medium text-gray-500">
                Logo
              </dt>
              <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <span class="grow">
                  <img
                    :src="team.photo_url"
                    :alt="team.name"
                    class="h-8 w-8 rounded-full object-cover">
                  <input
                    ref="photo"
                    type="file"
                    class="hidden"
                    @change="updatePhoto">
                </span>
                <template v-if="photoForm.processing">
                  Uploading
                </template>
                <template v-else>
                  <span class="ml-4 flex shrink-0 items-start space-x-4">
                    <button
                      v-if="can('update team')"
                      type="button"
                      class="rounded-md bg-white font-medium text-purple-600 hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                      @click.prevent="selectNewPhoto">
                      {{ team.photo_path ? 'Update' : 'Add' }}
                    </button>
                    <template v-if="team.photo_path">
                      <span
                        class="text-gray-300"
                        aria-hidden="true">|</span>
                      <button
                        type="button"
                        class="rounded-md bg-white font-medium text-purple-600 hover:text-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                        @click.prevent="deletePhoto">
                        Remove
                      </button>
                    </template>
                  </span>
                </template>
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt class="text-sm font-medium text-gray-500">
                Team e-mail
              </dt>
              <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <editable-field
                  v-if="can('update team')"
                  :form="form"
                  attribute="email"
                  @submit.prevent="updateTeam" />
                <span v-else>{{ team.email }}</span>
              </dd>
            </div>
            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5">
              <dt class="text-sm font-medium text-gray-500">
                Organization Number
              </dt>
              <dd class="mt-1 flex text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                <editable-field
                  v-if="can('update team')"
                  :form="form"
                  attribute="org_no"
                  @submit.prevent="updateTeam" />
                <span v-else>{{ team.org_no }}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <!-- Profile section end -->
      <div class="mt-10">
        <div class="space-y-1">
          <h3 class="text-lg font-medium leading-6 text-gray-900">
            Team brands
          </h3>
          <p class="max-w-2xl text-sm text-gray-500">
            Settings for your teams different brands.
          </p>
        </div>
        <div
          v-if="team.brands.length === 0"
          class="mt-6 flex flex-col items-center justify-center space-y-3">
          <p class="m-6 text-sm font-medium text-gray-500">
            No brands created yet
          </p>
        </div>
        <div
          v-else
          class="mt-6">
          <ul
            role="list"
            class="divide-y divide-gray-100">
            <li
              v-for="brand in team.brands"
              :key="brand.id"
              class="flex justify-between gap-x-6 py-5">
              <div class="flex min-w-0 gap-x-4">
                <div class="w-24">
                  <img
                    v-if="brand.logoUrl"
                    class="h-12 max-w-24 flex-none bg-gray-50"
                    :src="brand.logoUrl"
                    :alt="brand.name">
                </div>
                <div class="min-w-0 flex-auto">
                  <p class="py-3 text-sm font-semibold leading-6 text-gray-900">
                    {{ brand.name }}
                  </p>
                </div>
              </div>
              <div class="flex items-center space-x-10">
                <a
                  v-if="brand.terms"
                  :href="brand.terms.original_url"
                  target="_blank"
                  class="text-sm text-indigo-600 hover:text-indigo-800">Terms and Conditions</a>
                <p
                  v-if="brand.default"
                  class="text-sm leading-6 text-orange-500">
                  Default brand
                </p>
                <div class="hidden shrink-0 sm:flex sm:flex-col sm:items-end">
                  <p
                    v-if="can('edit brands')"
                    class="cursor-pointer text-sm leading-6 text-gray-900 hover:text-indigo-800"
                    @click="openEditBrandModal(brand)">
                    edit
                  </p>
                  <p
                    v-if="can('delete brands')"
                    class="mt-1 cursor-pointer text-xs leading-5 text-gray-500 hover:text-red-800"
                    @click="deleteBrand(brand.id)">
                    delete
                  </p>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <div
          class="mt-6 flex flex-col items-center justify-center space-y-3">
          <button
            v-if="can('add brand')"
            type="button"
            class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 sm:text-sm"
            @click="showBrandModal = true">
            New brand
          </button>
        </div>
      </div>
      <Modal
        :show="showBrandModal"
        max-width="lg"
        :closeable="true"
        @close="closeBrandModal">
        <div class="flex flex-col p-6">
          <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              @click="closeBrandModal">
              <span class="sr-only">Close</span>
              <svg
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form>
            <div class="space-y-12">
              <div class="border-b border-gray-900/10 pb-12">
                <h2 class="text-base font-semibold leading-7 text-gray-900">
                  Team Brand
                </h2>
                <p class="mt-1 text-sm leading-6 text-gray-600">
                  Create a Team brand to use with your proposals.
                </p>

                <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div class="sm:col-span-4">
                    <label
                      for="brand-name"
                      class="block text-sm font-medium leading-6 text-gray-900">Brand name</label>
                    <div class="mt-2">
                      <div class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                        <input
                          id="brand-create-name"
                          v-model="brandForm.name"
                          type="text"
                          name="brand"
                          class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Tour Operator, Inc">
                      </div>
                      <p
                        v-if="brandForm.errors.name"
                        class="mt-2 text-sm text-red-600">
                        {{ brandForm.errors.name }}
                      </p>
                    </div>
                  </div>

                  <div class="col-span-full">
                    <label
                      for="terms"
                      class="block text-sm font-medium leading-6 text-gray-900">Terms and conditions</label>
                    <input
                      id="brand-create-terms"
                      type="file"
                      accept="application/pdf"
                      @input="brandForm.terms = $event.target.files[0]">
                    <p
                      v-if="brandForm.errors.terms"
                      class="mt-2 text-sm text-red-600">
                      {{ brandForm.errors.terms }}
                    </p>
                  </div>
                  <div class="col-span-full">
                    <label
                      for="default"
                      class="block text-sm font-medium leading-6 text-gray-900">Set as default brand for your team</label>
                    <input
                      id="default"
                      v-model="brandForm.default"
                      type="checkbox"
                      class="focus:ring/50 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200">
                    <p
                      v-if="brandForm.errors.default"
                      class="mt-2 text-sm text-red-600">
                      {{ brandForm.errors.default }}
                    </p>
                  </div>
                  <div class="col-span-full">
                    <label
                      for="logo"
                      class="block text-sm font-medium leading-6 text-gray-900">Logo</label>
                    <div class="relative mt-2 flex items-center gap-x-3">
                      <MediaFigure
                        v-if="brandForm.media_crop"
                        :image="brandForm.media_crop.url"
                        image-overlay />

                      <MediaFigure
                        v-else
                        :image="brandForm.media"
                        :image-alt="brandForm.media?.description ?? ''"
                        image-overlay />
                      <div
                        class="absolute inset-0 w-full p-4 pb-24">
                        <media-select-single
                          :selected="brandForm.media"
                          :settings="{
                            all: {
                              name: 'Team Media',
                              get: route('api.team.media.index', { team: usePage().props.team.id }),
                              upload: route('api.team.media.store', { team: usePage().props.team.id }),
                            },
                          }"
                          :cropable="typeof brandForm.media !== 'string'"
                          :coordinates="brandForm.media_crop ? brandForm.media_crop.coordinates : null"
                          :include-default-tabs="false"
                          @update="updateMedia"
                          @crop="cropped" />
                      </div>
                      <p
                        v-if="brandForm.errors.media_id"
                        class="mt-2 text-sm text-red-600">
                        {{ brandForm.errors.media_id }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
              @click="createBrand">
              Create
            </button>
            <button
              type="button"
              class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              @click="closeBrandModal">
              Cancel
            </button>
          </div>
        </div>
      </modal>

      <Modal
        :show="editBrandForm.id !== null"
        max-width="lg"
        :closeable="true"
        @close="closeEditBrandModal">
        <div class="flex flex-col p-6">
          <div class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
            <button
              type="button"
              class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              @click="closeEditBrandModal">
              <span class="sr-only">Close</span>
              <svg
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form>
            <div class="space-y-12">
              <div class="border-b border-gray-900/10 pb-12">
                <h2 class="text-base font-semibold leading-7 text-gray-900">
                  Team Brand
                </h2>
                <p class="mt-1 text-sm leading-6 text-gray-600">
                  Update brand.
                </p>

                <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div class="sm:col-span-4">
                    <label
                      for="brand-name"
                      class="block text-sm font-medium leading-6 text-gray-900">Brand name</label>
                    <div class="mt-2">
                      <div class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                        <input
                          id="brand-edit-name"
                          v-model="editBrandForm.name"
                          type="text"
                          name="brand"
                          class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Tour Operator, Inc">
                      </div>
                      <p
                        v-if="editBrandForm.errors.name"
                        class="mt-2 text-sm text-red-600">
                        {{ editBrandForm.errors.name }}
                      </p>
                    </div>
                  </div>
                  <div class="col-span-full">
                    <label
                      for="terms"
                      class="block text-sm font-medium leading-6 text-gray-900">Terms and conditions</label>
                    <div class="mb-5">
                      <p
                        v-if="editBrandForm.terms_media_id"
                        class="text-xs">
                        Terms and Conditions.pdf
                        <span
                          class="cursor-pointer text-indigo-500 hover:text-indigo-800"
                          @click="editBrandForm.terms_media_id = null">remove or replace</span>
                      </p>
                      <input
                        v-else
                        id="brand-edit-terms"
                        type="file"
                        accept="application/pdf"
                        @input="editBrandForm.terms = $event.target.files[0]">
                      <p
                        v-if="editBrandForm.errors.terms"
                        class="mt-2 text-sm text-red-600">
                        {{ editBrandForm.errors.terms }}
                      </p>
                    </div>
                    <div class="col-span-full">
                      <label
                        for="default"
                        class="block text-sm font-medium leading-6 text-gray-900">Set as default brand for your team</label>
                      <input
                        id="default"
                        v-model="editBrandForm.default"
                        type="checkbox"
                        class="focus:ring/50 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200">
                      <p
                        v-if="editBrandForm.errors.default"
                        class="mt-2 text-sm text-red-600">
                        {{ editBrandForm.errors.default }}
                      </p>
                    </div>
                    <div class="col-span-full mt-4">
                      <label
                        for="logo"
                        class="block text-sm font-medium leading-6 text-gray-900">Logo</label>
                      <div class="relative mt-2 flex items-center gap-x-3">
                        <MediaFigure
                          v-if="editBrandForm.media_crop"
                          :image="editBrandForm.media_crop.url"
                          image-overlay />

                        <MediaFigure
                          v-else
                          :image="editBrandForm.media"
                          :image-alt="editBrandForm.media?.description ?? ''"
                          image-overlay />
                        <div
                          class="absolute inset-0 w-full p-4 pb-24">
                          <media-select-single
                            :selected="editBrandForm.media"
                            :settings="{
                              all: {
                                name: 'Team Media',
                                get: route('api.team.media.index', { team: usePage().props.team.id }),
                                upload: route('api.team.media.store', { team: usePage().props.team.id }),
                              },
                            }"
                            :cropable="typeof editBrandForm.media !== 'string'"
                            :coordinates="editBrandForm.media_crop ? editBrandForm.media_crop.coordinates : null"
                            :include-default-tabs="false"
                            @update="editUpdateMedia"
                            @crop="editCropped" />
                        </div>
                        <p
                          v-if="editBrandForm.errors.media_id"
                          class="mt-2 text-sm text-red-600">
                          {{ editBrandForm.errors.media_id }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
              @click="saveBrand">
              Save
            </button>
            <button
              type="button"
              class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              @click="closeEditBrandModal">
              Cancel
            </button>
          </div>
        </div>
      </modal>
    </div>
  </Wrapper>
</template>

<script>
import Modal from '@/Jetstream/Modal.vue';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';
import { usePage } from '@inertiajs/vue3';
import MediaFigure from '@/Pages/Proposal/Components/MediaFigure.vue';
import { router as Inertia } from '@inertiajs/core';
import EditableField from '../../../ComponentsDeprecated/EditableField.vue';
import Wrapper from './Wrapper.vue';

export default {
  components: {
    MediaFigure,
    MediaSelectSingle,
    Modal,
    EditableField,
    Wrapper,
  },

  props: {
    team: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        name: this.team.name,
        email: this.team.email,
        org_no: this.team.org_no,
      }),

      photoForm: this.$inertia.form({
        _method: 'PUT',
        photo: null,
      }),

      brandForm: this.$inertia.form({
        name: '',
        media: null,
        media_crop: null,
        terms: null,
        default: false,
      }),

      editBrandForm: this.$inertia.form({
        id: null,
        name: null,
        media: null,
        media_crop: null,
        terms: null,
        terms_media_id: null,
        default: false,
      }),

      showBrandModal: false,
    };
  },

  methods: {
    usePage,
    updateTeam() {
      this.form.put(route('teams.update', this.team), {
        errorBag: 'updateTeam',
        preserveScroll: true,
      });
    },

    selectNewPhoto() {
      this.$refs.photo.click();
    },

    updatePhoto() {
      if (this.$refs.photo) {
        this.photoForm.photo = this.$refs.photo.files[0];
      }
      this.photoForm.post(route('team-photo.update', this.team), {
        errorBag: 'updateTeamPhoto',
        preserveScroll: true,
        onSuccess: () => (this.clearPhotoFileInput()),
      });
    },

    deletePhoto() {
      this.$inertia.delete(route('team-photo.destroy', this.team), {
        preserveScroll: true,
        onSuccess: () => {
          this.photoPreview = null;
          this.clearPhotoFileInput();
        },
      });
    },

    closeBrandModal() {
      this.showBrandModal = false;
      this.brandForm.reset();
    },

    closeEditBrandModal() {
      this.editBrandForm.reset();
    },

    openEditBrandModal(brand) {
      this.editBrandForm.id = brand.id;
      this.editBrandForm.name = brand.name;
      this.editBrandForm.media = brand.logo;
      this.editBrandForm.media_crop = brand.media_crop;
      this.editBrandForm.terms_media_id = brand.terms_media_id;
      this.editBrandForm.default = brand.default === 1;
    },

    createBrand() {
      this.brandForm.transform((data) => ({
        name: data.name,
        media_id: data.media?.id,
        media_crop: data.media_crop,
        terms: data.terms,
        default: data.default,
      })).post(route('api.brand.store'), {
        preserveScroll: true,
        onSuccess: () => this.closeBrandModal(),
      });
    },

    saveBrand() {
      this.editBrandForm.transform((data) => ({
        name: data.name,
        media_id: data.media?.id,
        media_crop: data.media_crop,
        terms_media_id: data.terms_media_id,
        terms: data.terms,
        default: data.default,
        _method: 'put',
      })).post(route('api.brand.update', { brand: this.editBrandForm.id }), {
        preserveScroll: true,
        onSuccess: () => this.closeEditBrandModal(),
      });
    },

    deleteBrand(id) {
      Inertia.delete(route('api.brand.destroy', {
        brand: id,
      }));
    },

    updateMedia(media) {
      this.brandForm.media = media;
      this.brandForm.media_crop = null;
    },

    cropped(data) {
      this.brandForm.media_crop = data;
    },

    editUpdateMedia(media) {
      this.editBrandForm.media = media;
      this.editBrandForm.media_crop = null;
    },

    editCropped(data) {
      this.editBrandForm.media_crop = data;
    },

    clearPhotoFileInput() {
      if (this.$refs.photo?.value) {
        this.$refs.photo.value = null;
      }
    },
  },
};
</script>
